import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { events, pastEvents } from '../data/events';
import { EventCard } from './EventCard';
type Tab = 'upcoming' | 'past';
const EASE = [0.23, 1, 0.32, 1] as const;
const tabs: {
  id: Tab;
  label: string;
}[] = [{
  id: 'upcoming',
  label: 'Upcoming'
}, {
  id: 'past',
  label: 'Past'
}];
export function EventsStrip() {
  const scrollerRef = useRef<HTMLUListElement>(null);
  const [tab, setTab] = useState<Tab>('upcoming');
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const list = tab === 'upcoming' ? events : pastEvents;
  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [updateEdges]);
  useEffect(() => {
    scrollerRef.current?.scrollTo({
      left: 0
    });
    updateEdges();
  }, [tab, updateEdges]);
  const scroll = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector('li');
    const step = card ? card.getBoundingClientRect().width + 20 : 320;
    el.scrollBy({
      left: dir * step,
      behavior: 'smooth'
    });
  };
  const arrowClass = 'rounded-full border-2 border-ink p-3 text-ink transition-[transform,background-color,color,opacity] duration-150 ease-out hover:bg-ink hover:text-sand active:scale-[0.95] disabled:pointer-events-none disabled:opacity-30';
  return <section id="events" className="border-t border-ink/10 bg-sand py-16 md:py-20" aria-labelledby="events-title" data-id="element-77" data-mp="2r54n1y8h5t1e">
      <div className="mx-auto max-w-7xl px-5 md:px-8" data-id="element-78" data-mp="2m69xmdg0568b">
        <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-79" data-mp="2m6tbnj2j7ah2">
          <div data-id="element-80" data-mp="kg2n369j0vkj">
            <p className="font-script text-3xl text-flamingo" data-id="element-81" data-mp="kf9lsktc6y9s">
              {tab === 'upcoming' ? 'Próximos eventos' : 'Eventos pasados'}
            </p>
            <h2 id="events-title" className="mt-1 font-display text-5xl uppercase leading-none text-ink md:text-6xl" data-id="element-82" data-mp="aj6mrnts7fr2">
              {tab === 'upcoming' ? 'Upcoming Events' : 'Past Events'}
            </h2>
          </div>

          <div className="flex items-center gap-4" data-id="element-83" data-mp="138dmur8vwdef">
            <div className="flex rounded-full bg-dune p-1" role="tablist" aria-label="Event timeframe" data-id="element-84" data-mp="186f3l5iu80mq">
              {tabs.map(t => {
              const active = t.id === tab;
              return <button key={t.id} type="button" role="tab" aria-selected={active} onClick={() => setTab(t.id)} className={`relative whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition-colors duration-150 ${active ? 'text-sand' : 'text-ink/70 hover:text-ink'}`} data-id="element-85" data-mp="y7zd94rmwvea">
                    {active && <motion.span layoutId="events-tab" className="absolute inset-0 rounded-full bg-ink" transition={{
                  duration: 0.22,
                  ease: EASE
                }} data-id="element-86" data-mp="3lcv7hkvdzfo9" />}
                    <span className="relative" data-id="element-87" data-mp="2smd8auxs9yg4">{t.label}</span>
                  </button>;
            })}
            </div>
            <div className="flex gap-2" data-id="element-88" data-mp="32esq9s92yn10">
              <button type="button" onClick={() => scroll(-1)} disabled={atStart} aria-label="Previous events" className={arrowClass} data-id="element-89" data-mp="32fu424hhdnmf">
                <ArrowLeftIcon size={18} data-id="element-90" data-mp="29xzt800njg88" />
              </button>
              <button type="button" onClick={() => scroll(1)} disabled={atEnd} aria-label="Next events" className={arrowClass} data-id="element-91" data-mp="29w592nx7fs57">
                <ArrowRightIcon size={18} data-id="element-92" data-mp="187i3rbyrkvln" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <motion.ul key={tab} ref={scrollerRef} onScroll={updateEdges} initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} className="mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-5 pb-2 [scrollbar-width:none] md:px-[max(2rem,calc((100vw-80rem)/2+2rem))] [&::-webkit-scrollbar]:hidden" data-id="element-93" data-mp="1c8ljzzkq4tne">
        {list.map((event, i) => <EventCard key={event.id} event={event} index={i} past={tab === 'past'} data-id="element-94" data-mp="1vz6y0xv5bu58" data-mpi={i} />)}
      </motion.ul>
    </section>;
}